import { useState, useEffect } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';

export interface QuickBooksConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  realmId?: string; // QuickBooks Company ID
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: number;
  scope?: string;
  isClientSpecific?: boolean;
}

export interface QuickBooksInvoice {
  id: string;
  invoiceNumber: string;
  amount: number;
  balance: number;
  dueDate: string;
  customerRef: {
    value: string;
    name: string;
  };
}

export interface QuickBooksCompany {
  id: string;
  name: string;
}

export function useQuickBooks() {
  const [config, setConfig] = useState<QuickBooksConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const configRef = doc(db, 'config/quickbooks');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        setConfig(configSnap.data() as QuickBooksConfig);
      } else {
        setConfig(null);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading QuickBooks config:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (newConfig: QuickBooksConfig) => {
    try {
      const configRef = doc(db, 'config/quickbooks');
      await setDoc(configRef, newConfig, { merge: true });
      setConfig(newConfig);
    } catch (err) {
      console.error('Error saving QuickBooks config:', err);
      throw err;
    }
  };

  const initiateOAuthFlow = (): string => {
    if (!config?.clientId) {
      throw new Error('QuickBooks OAuth configuration incomplete');
    }

    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('quickbooks_oauth_state', state);

    const scope = 'com.intuit.quickbooks.accounting';
    const authBaseUrl = 'https://appcenter.intuit.com/connect/oauth2';

    const params = new URLSearchParams({
      client_id: config.clientId,
      scope: scope,
      redirect_uri: config.redirectUri,
      response_type: 'code',
      state: state,
    });

    return `${authBaseUrl}?${params.toString()}`;
  };

  const handleOAuthCallback = async (code: string, state: string | null, realmId?: string | null): Promise<void> => {
    try {
      const savedState = sessionStorage.getItem('quickbooks_oauth_state');

      if (!state || !savedState) {
        throw new Error('Missing state parameter');
      }

      if (state !== savedState) {
        throw new Error('State parameter mismatch');
      }

      sessionStorage.removeItem('quickbooks_oauth_state');

      if (!config?.clientId || !config?.clientSecret) {
        throw new Error('QuickBooks OAuth configuration incomplete');
      }

      // Call cloud function to exchange authorization code for tokens
      const exchangeQuickBooksToken = httpsCallable(functions, 'exchangeQuickBooksToken');
      await exchangeQuickBooksToken({
        code,
        redirectUri: config.redirectUri,
      });

      // Reload config to get the updated tokens
      await loadConfig();

      // If realmId was provided, update it in the config
      if (realmId && config) {
        await saveConfig({ ...config, realmId });
      }
    } catch (err: any) {
      console.error('Error handling OAuth callback:', err);
      // Provide more user-friendly error messages
      if (err.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to connect QuickBooks');
      } else if (err.code === 'functions/failed-precondition') {
        throw new Error('QuickBooks configuration is incomplete. Please check your settings.');
      } else if (err.message) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const disconnect = async () => {
    try {
      const configRef = doc(db, 'config/quickbooks');
      await updateDoc(configRef, {
        accessToken: null,
        refreshToken: null,
        tokenExpiresAt: null,
        realmId: null,
      });
      await loadConfig();
    } catch (err) {
      console.error('Error disconnecting QuickBooks:', err);
      throw err;
    }
  };

  const getOpenInvoices = async (realmId: string): Promise<QuickBooksInvoice[]> => {
    if (!config?.accessToken) {
      throw new Error('Not authenticated with QuickBooks');
    }

    try {
      const response = await fetch(
        `https://quickbooks.api.intuit.com/v3/company/${realmId}/query?query=SELECT * FROM Invoice WHERE Balance > 0`,
        {
          headers: {
            Authorization: `Bearer ${config.accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch QuickBooks invoices');
      }

      const data = await response.json();
      return data.QueryResponse?.Invoice || [];
    } catch (err) {
      console.error('Error fetching QuickBooks invoices:', err);
      throw err;
    }
  };

  const linkClientToCompany = async (clientId: string, companyId: string) => {
    try {
      const clientRef = doc(db, 'clients', clientId);
      await updateDoc(clientRef, {
        quickbooksCompanyId: companyId,
      });
    } catch (err) {
      console.error('Error linking client to QuickBooks company:', err);
      throw err;
    }
  };

  return {
    config,
    loading,
    error,
    saveConfig,
    initiateOAuthFlow,
    handleOAuthCallback,
    disconnect,
    getOpenInvoices,
    linkClientToCompany,
  };
}
